import { cn } from "@/lib/utils";

const iconSupport = "/assets/images/figma/recipe/icon-support.svg";
const badgeLeftBlue = "/assets/images/figma/recipe/badge-left-blue.svg";
const badgeRightBlue = "/assets/images/figma/recipe/badge-right-blue.svg";
const badgeLeftOrange = "/assets/images/figma/recipe/badge-left-orange.svg";
const badgeRightOrange = "/assets/images/figma/recipe/badge-right-orange.svg";

type RecipeSpecBadgeGroupProps = {
  label: string;
  value: string;
  variant: "blue" | "orange";
  className?: string;
};

/** Storage / RAM badge cluster (icon + label + bracket pill) — Figma recipe rows */
export const RecipeSpecBadgeGroup: React.FC<RecipeSpecBadgeGroupProps> = ({
  label,
  value,
  variant,
  className
}) => {
  const isBlue = variant === "blue";
  return (
    <div className={cn("flex items-center gap-[12px]", className)}>
      <div className="flex items-center gap-[8px]">
        <div className="relative size-[20px] shrink-0 overflow-clip">
          <img alt="" className="absolute inset-0 block size-full max-w-none" src={iconSupport} />
        </div>
        <p className="label-small whitespace-nowrap font-medium text-foreground">{label}</p>
      </div>
      <div className="flex items-center">
        <div className="relative h-[20px] w-[10px] shrink-0">
          <div className="absolute inset-[0_0_0_16.06%]">
            <img
              alt=""
              className="block size-full max-w-none"
              src={isBlue ? badgeLeftBlue : badgeLeftOrange}
            />
          </div>
        </div>
        <div
          className={cn(
            "flex h-[20px] items-center justify-center px-[2px]",
            isBlue ? "bg-[rgba(49,121,255,0.1)]" : "bg-[rgba(229,123,46,0.1)]"
          )}
        >
          <p
            className={cn(
              "label-small whitespace-nowrap text-center font-medium",
              isBlue ? "text-[#3179ff]" : "text-[#e57b2e]"
            )}
          >
            {value}
          </p>
        </div>
        <div className="relative h-[20px] w-[4px] shrink-0">
          <img
            alt=""
            className="absolute inset-0 block size-full max-w-none"
            src={isBlue ? badgeRightBlue : badgeRightOrange}
          />
        </div>
      </div>
    </div>
  );
};

/** 24px radio — Figma nodes 1:5153 / RecipeCard */
export const RecipeRadio24: React.FC<{ isSelected: boolean }> = ({ isSelected }) => (
  <div
    className="inline-grid shrink-0 place-items-start leading-[0]"
    style={{ gridTemplateColumns: "max-content", gridTemplateRows: "max-content" }}
  >
    <div className="col-[1] row-[1] size-[24px] rounded-[56px] border border-border bg-background" />
    {isSelected && (
      <div className="col-[1] row-[1] ml-[7px] mt-[7px] size-[10px] rounded-[100px] bg-primary" />
    )}
  </div>
);

/** Centered Storage | divider | RAM row with aligned columns — Figma dialog node 1:5154 */
export const RecipeDialogStorageRamRow: React.FC<{ storage: string; ram: string }> = ({
  storage,
  ram
}) => (
  <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center border border-border bg-background">
    <div className="flex items-center justify-end py-2 pl-4 pr-3">
      <RecipeSpecBadgeGroup label="Storage" value={storage} variant="blue" />
    </div>
    <div className="h-9 w-[1.25px] shrink-0 bg-border" aria-hidden />
    <div className="flex items-center justify-start py-2 pl-3 pr-4">
      <RecipeSpecBadgeGroup label="RAM" value={ram} variant="orange" />
    </div>
  </div>
);
